import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import PaymentService, {
  PaymentHistory,
  CreditBalance,
} from "../api/Payment/PaymentService";

import { AxiosError } from "axios";

interface PaymentState {
  isStripeConnected: boolean;
  stripeConnectedDate: string | null;
  creditBalance: number;
  totalCreditsPurchased: number;
  paymentHistory: PaymentHistory[];
  loading: boolean;
  error: string | null;
}

const initialState: PaymentState = {
  isStripeConnected: false,
  stripeConnectedDate: null,
  creditBalance: 0,
  totalCreditsPurchased: 0,
  paymentHistory: [],
  loading: false,
  error: null,
};

const formatDecimal = (num: number): number => {
  return Number(num.toFixed(2));
};

// Helper function to handle errors
const handleError = (error: unknown) => {
  if (error instanceof AxiosError) {
    return error.response?.data?.error || error.message;
  }
  return "An unexpected error occurred";
};

export const checkStripeConnection = createAsyncThunk(
  "payment/checkStripeConnection",
  async (_, { rejectWithValue }) => {
    try {
      const response = await PaymentService.checkStripeConnection();
      return response.isConnected;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const connectStripe = createAsyncThunk(
  "payment/connectStripe",
  async (_, { rejectWithValue }) => {
    try {
      await PaymentService.connectStripe();
      return true;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const disconnectStripe = createAsyncThunk(
  "payment/disconnectStripe",
  async (_, { rejectWithValue }) => {
    try {
      await PaymentService.disconnectStripe();
      return true;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const fetchCreditBalance = createAsyncThunk<CreditBalance, void>(
  "payment/fetchCreditBalance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await PaymentService.getCreditBalance();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const fetchPaymentHistory = createAsyncThunk(
  "payment/fetchPaymentHistory",
  async (_, { rejectWithValue }) => {
    try {
      const response = await PaymentService.getPaymentHistory();
      return response.history;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const purchaseCredits = createAsyncThunk(
  "payment/purchaseCredits",
  async (quantity: number, { dispatch, rejectWithValue }) => {
    try {
      await PaymentService.purchaseCredits(quantity);
      await dispatch(fetchPaymentHistory());
      await dispatch(fetchCreditBalance());
      return true;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  },
);

const paymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Check Stripe Connection
      .addCase(checkStripeConnection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkStripeConnection.fulfilled, (state, action) => {
        state.isStripeConnected = action.payload;
        if (action.payload) {
          state.stripeConnectedDate = new Date().toISOString();
        }
        state.loading = false;
      })
      .addCase(checkStripeConnection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Connect Stripe
      .addCase(connectStripe.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(connectStripe.fulfilled, (state) => {
        state.isStripeConnected = true;
        state.stripeConnectedDate = new Date().toISOString();
        state.loading = false;
      })
      .addCase(connectStripe.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Disconnect Stripe
      .addCase(disconnectStripe.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(disconnectStripe.fulfilled, (state) => {
        state.isStripeConnected = false;
        state.stripeConnectedDate = null;
        state.loading = false;
      })
      .addCase(disconnectStripe.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Credit Balance
      .addCase(fetchCreditBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCreditBalance.fulfilled, (state, action) => {
        state.creditBalance = formatDecimal(action.payload.credit_balance);
        state.totalCreditsPurchased = formatDecimal(
          action.payload.total_credits_purchased,
        );
        state.loading = false;
      })
      .addCase(fetchCreditBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Payment History
      .addCase(fetchPaymentHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentHistory.fulfilled, (state, action) => {
        state.paymentHistory = action.payload || [];
        state.loading = false;
      })
      .addCase(fetchPaymentHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Purchase Credits
      .addCase(purchaseCredits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(purchaseCredits.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(purchaseCredits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetError } = paymentSlice.actions;
export default paymentSlice.reducer;
